import { ChangeDetectionStrategy, Component, InjectionToken, computed, inject, signal, type OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { CloudApiService, type OAuthConsentRequest } from '@/cloud/services/cloud-api.service';

/**
 * Whether the page is running inside a frame. The consent page refuses to offer Allow when framed,
 * so another site cannot overlay it and trick a click (clickjacking). A token so tests — which
 * Karma runs inside an iframe — can say no.
 */
export const CONSENT_FRAMED = new InjectionToken<boolean>('CONSENT_FRAMED', {
    providedIn: 'root',
    factory: () => {
        try {
            return window.top !== window.self;
        } catch {
            return true;
        }
    }
});

/**
 * OAuth consent for AI agents (MCP). `/oauth/authorize` on the API stores the request and sends
 * the browser here with `?request=<id>`; the signed-in user picks which tenants the app may read
 * (one → tenant-scoped, several → multi-tenant) and approves or denies. The API answers with the
 * app's redirect URL (carrying the code, or `error=access_denied`) and the browser follows it.
 *
 * The app's name is self-declared at registration, so the redirect host is shown as the thing
 * the user can actually verify.
 */
@Component({
    selector: 'app-oauth-consent',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, MessageModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="bg-surface-50 dark:bg-surface-950 flex items-center justify-center min-h-screen p-4">
            <div class="bg-surface-0 dark:bg-surface-900 w-full rounded-border shadow p-6 sm:p-8 flex flex-col gap-4" style="max-width: 34rem">
                @if (loading()) {
                    <div class="text-color-secondary">Loading…</div>
                } @else if (error()) {
                    <h1 class="text-2xl font-semibold m-0">Can't continue</h1>
                    <p-message severity="error" class="w-full">{{ error() }}</p-message>
                } @else if (request(); as r) {
                    <div>
                        <h1 class="text-2xl font-semibold m-0 mb-1">Connect {{ r.clientName }}</h1>
                        <p class="m-0 text-color-secondary">
                            <strong>{{ r.clientName }}</strong> wants <strong>read-only</strong> access to devices, locations, logs, configuration, software versions and connected equipment in the tenants you choose. It never sees credential values and cannot change anything.
                        </p>
                    </div>

                    <p-message severity="warn" class="w-full">
                        Only continue if you just started connecting this app. You'll be sent back to <strong>{{ r.redirectHost }}</strong
                        >.
                    </p-message>

                    <div>
                        <div class="flex items-center justify-between mb-2">
                            <span class="font-medium">Tenants</span>
                            @if (eligible().length > 1) {
                                <button pButton type="button" class="p-button-text p-button-sm" (click)="toggleAll()">
                                    <span class="p-button-label">{{ allSelected() ? 'Clear' : 'Select all' }}</span>
                                </button>
                            }
                        </div>
                        @if (r.namespaces.length === 0) {
                            <div class="text-color-secondary text-sm">You don't have access to any tenant.</div>
                        }
                        <ul class="list-none p-0 m-0 flex flex-col gap-1">
                            @for (ns of r.namespaces; track ns.namespaceId) {
                                <li class="flex items-center gap-2 p-2 rounded-border" [class.opacity-60]="!ns.mcpEnabled">
                                    <p-checkbox [inputId]="'ns-' + ns.namespaceId" [binary]="true" [disabled]="!ns.mcpEnabled" [ngModel]="selected().has(ns.namespaceId)" (ngModelChange)="toggle(ns.namespaceId, $event)" />
                                    <label [for]="'ns-' + ns.namespaceId" class="flex-1">
                                        {{ ns.name }}
                                        @if (!ns.mcpEnabled) {
                                            <small class="block text-color-secondary">AI agent access is off for this tenant — ask a tenant admin to enable it.</small>
                                        }
                                    </label>
                                </li>
                            }
                        </ul>
                        <small class="text-color-secondary block mt-2">{{ scopeSummary() }}</small>
                    </div>

                    <div class="flex gap-2 justify-end">
                        <button pButton type="button" class="p-button-text" (click)="deny()" [disabled]="submitting()"><span class="p-button-label">Deny</span></button>
                        <button pButton type="button" (click)="approve()" [disabled]="submitting() || selected().size === 0"><span class="p-button-label">Allow</span></button>
                    </div>
                    <small class="text-color-secondary">You can disconnect the app at any time from your profile.</small>
                }
            </div>
        </div>
    `
})
export class OAuthConsent implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private framed = inject(CONSENT_FRAMED);

    request = signal<OAuthConsentRequest | null>(null);
    selected = signal<Set<string>>(new Set());
    loading = signal(true);
    submitting = signal(false);
    error = signal<string | null>(null);

    eligible = computed(() => (this.request()?.namespaces ?? []).filter((n) => n.mcpEnabled));
    allSelected = computed(() => this.eligible().length > 0 && this.eligible().every((n) => this.selected().has(n.namespaceId)));
    scopeSummary = computed(() => {
        const count = this.selected().size;
        if (count === 0) return 'Choose at least one tenant.';
        return count === 1 ? 'The app will be limited to this one tenant.' : `The app will be able to read ${count} tenants.`;
    });

    /** Replaced in tests; the real one leaves the SPA for the app's redirect URI. */
    navigate: (url: string) => void = (url) => window.location.assign(url);

    ngOnInit(): void {
        if (this.framed) {
            this.fail('For your security this page cannot be shown inside another site. Open it directly in your browser.');
            return;
        }
        const id = this.route.snapshot.queryParamMap.get('request');
        if (!id) {
            this.fail('This link is incomplete. Start connecting again from the app.');
            return;
        }
        this.api
            .getOAuthConsentRequest(id)
            .then((r) => {
                this.request.set(r);
                const eligible = r.namespaces.filter((n) => n.mcpEnabled);
                if (eligible.length === 1) this.selected.set(new Set([eligible[0].namespaceId]));
            })
            .catch((err) => this.fail(this.messageOf(err, 'This request has expired or was already used. Start connecting again from the app.')))
            .finally(() => this.loading.set(false));
    }

    toggle(namespaceId: string, on: boolean) {
        const next = new Set(this.selected());
        if (on) next.add(namespaceId);
        else next.delete(namespaceId);
        this.selected.set(next);
    }

    toggleAll() {
        this.selected.set(this.allSelected() ? new Set() : new Set(this.eligible().map((n) => n.namespaceId)));
    }

    approve() {
        const r = this.request();
        if (!r) return;
        this.submitting.set(true);
        this.api
            .approveOAuthConsentRequest(r.requestId, Array.from(this.selected()))
            .then((res) => this.navigate(res.redirectUrl))
            .catch((err) => {
                this.submitting.set(false);
                this.fail(this.messageOf(err, 'Could not approve the request.'));
            });
    }

    deny() {
        const r = this.request();
        if (!r) return;
        this.submitting.set(true);
        this.api
            .denyOAuthConsentRequest(r.requestId)
            .then((res) => this.navigate(res.redirectUrl))
            .catch((err) => {
                this.submitting.set(false);
                this.fail(this.messageOf(err, 'Could not deny the request.'));
            });
    }

    private fail(message: string) {
        this.error.set(message);
        this.loading.set(false);
    }

    private messageOf(err: any, fallback: string): string {
        return err?.error?.message ?? fallback;
    }
}
